import React from 'react';
import styled from 'styled-components';
import * as FiIcons from 'react-icons/fi';
import { DEPLOYED_APPS } from '../../config/deployedApps';
import AppCard from './AppCard';
import { LandingShell, Container, PrimaryLink, GhostLink, Eyebrow, H2, Sub, T } from './ui';

const FiArrowRight = FiIcons.FiArrowRight as React.ComponentType<any>;
const FiSend = FiIcons.FiSend as React.ComponentType<any>;
const FiCheckCircle = FiIcons.FiCheckCircle as React.ComponentType<any>;
const FiLogIn = FiIcons.FiLogIn as React.ComponentType<any>;

/**
 * ServiceBuds products page (`/products`). Markets the deployed
 * products and routes visitors into the preview-access flow: request → team
 * approval → sign in. It no longer grants instant preview (see /signup wizard).
 */
const ProductsLanding: React.FC = () => (
  <LandingShell>
    <Container>
      <Hero>
        <BlobA /><BlobB />
        <HeroInner>
          <Pill>ServiceBuds suite · 4 products live</Pill>
          <H1>Operations software, built for the work you actually do.</H1>
          <Lead>
            ServiceBuds brings property management, pest-control operations, workplace
            safety, and clinical care into one platform. Request preview access, our team
            approves you, and you sign in to exactly the apps you’re cleared for.
          </Lead>
          <Actions>
            <PrimaryLink to="/signup">Request preview access <FiArrowRight /></PrimaryLink>
            <GhostLink to="/signin">Sign in</GhostLink>
          </Actions>
        </HeroInner>
      </Hero>

      <Section>
        <Head>
          <Eyebrow>Product offerings</Eyebrow>
          <H2>Three products, one suite</H2>
          <Sub>Each app is deployed and ready. Preview access unlocks the ones the team clears for you.</Sub>
        </Head>
        <CardGrid>
          {DEPLOYED_APPS.map(app => <AppCard key={app.id} app={app} />)}
        </CardGrid>
      </Section>

      <Section>
        <Head>
          <Eyebrow>How preview access works</Eyebrow>
          <H2>Request, get approved, sign in</H2>
        </Head>
        <Steps>
          <Step>
            <StepIcon $bg="#E6F2F0" $fg={T.accent}><FiSend /></StepIcon>
            <StepTitle>1 · Request access</StepTitle>
            <StepText>Complete a short signup wizard and pick the apps you want to try.</StepText>
          </Step>
          <Step>
            <StepIcon $bg="#FBF1DD" $fg="#9A6510"><FiCheckCircle /></StepIcon>
            <StepTitle>2 · Team review</StepTitle>
            <StepText>Our team reviews your request and approves the apps you’re cleared for.</StepText>
          </Step>
          <Step>
            <StepIcon $bg="#E3F3EC" $fg="#1B7A52"><FiLogIn /></StepIcon>
            <StepTitle>3 · Sign in & preview</StepTitle>
            <StepText>Once approved, sign in to open your personalized set of apps.</StepText>
          </Step>
        </Steps>
      </Section>

      <CtaBand>
        <div>
          <CtaTitle>Ready to see ServiceBuds in action?</CtaTitle>
          <CtaSub>It takes about a minute to request access.</CtaSub>
        </div>
        <PrimaryLink to="/signup">Start your request <FiArrowRight /></PrimaryLink>
      </CtaBand>
    </Container>
  </LandingShell>
);

export default ProductsLanding;

// ── styled ────────────────────────────────────────────────────────────────────

const Hero = styled.header`
  position: relative; overflow: hidden;
  margin-top: 8px; border-radius: 26px;
  background: ${T.hero};
  box-shadow: 0 12px 30px rgba(14,63,58,.30);
`;
const BlobA = styled.div`position:absolute; top:-90px; right:-50px; width:300px; height:300px; border-radius:50%; background:rgba(80,190,175,.18);`;
const BlobB = styled.div`position:absolute; bottom:-120px; left:20%; width:280px; height:280px; border-radius:50%; background:rgba(80,190,175,.10);`;
const HeroInner = styled.div`position: relative; z-index: 1; padding: 56px 40px 60px; max-width: 720px;`;

const Pill = styled.span`
  display: inline-block; font-size: 12px; font-weight: 700; color: #CFEDE6;
  background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.20);
  border-radius: 20px; padding: 6px 12px; margin-bottom: 18px;
`;
const H1 = styled.h1`
  font-size: 40px; line-height: 1.08; font-weight: 800; letter-spacing: -.025em;
  color: #fff; margin: 0 0 14px;
  @media (max-width: 640px) { font-size: 30px; }
`;
const Lead = styled.p`font-size: 16px; line-height: 1.6; color: rgba(214,240,235,.92); margin: 0 0 26px;`;
const Actions = styled.div`display: flex; gap: 12px; flex-wrap: wrap;`;

const Section = styled.section`padding: 48px 0 8px;`;
const Head = styled.div`margin-bottom: 22px;`;
const CardGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 20px;`;

const Steps = styled.div`display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 16px;`;
const Step = styled.div`
  background: ${T.surface}; border: 1px solid ${T.border}; border-radius: 18px; padding: 22px;
  box-shadow: 0 4px 14px rgba(13,52,47,.05);
`;
interface IcoProps { $bg: string; $fg: string; }
const StepIcon = styled.div<IcoProps>`
  width: 42px; height: 42px; border-radius: 12px; display: flex; align-items: center; justify-content: center;
  font-size: 20px; color: ${p => p.$fg}; background: ${p => p.$bg}; margin-bottom: 12px;
`;
const StepTitle = styled.h3`font-size: 15px; font-weight: 800; color: ${T.text}; margin: 0 0 5px;`;
const StepText = styled.p`font-size: 13.5px; line-height: 1.5; color: ${T.text2}; margin: 0;`;

const CtaBand = styled.div`
  margin: 40px 0 8px; padding: 26px 30px; border-radius: 20px;
  background: ${T.hero}; color: #fff;
  display: flex; align-items: center; justify-content: space-between; gap: 18px; flex-wrap: wrap;
  box-shadow: 0 10px 24px rgba(14,63,58,.28);
`;
const CtaTitle = styled.div`font-size: 20px; font-weight: 800; letter-spacing: -.01em;`;
const CtaSub = styled.div`font-size: 13.5px; color: rgba(214,240,235,.9); margin-top: 2px;`;
